<section class="text-fg w-full text-fg">
  <header class="mb-5 flex flex-wrap items-end justify-between gap-5">
    <div>
      <h1 class="m-0 font-heading text-3xl font-extrabold tracking-tight">Fees Hub</h1>
      <p class="mb-0 mt-1 text-sm font-medium text-muted">Full BI view for fee revenue, provider cost, merchant paid totals, and configuration visibility.</p>
    </div>
    <div class="flex gap-1 rounded-xl border border-border bg-surface-muted p-1">
      <button type="button" uiButton size="sm" [variant]="period === 'today' ? 'primary' : 'ghost'" (click)="applyPeriod('today')">Today</button>
      <button type="button" uiButton size="sm" [variant]="period === 'week' ? 'primary' : 'ghost'" (click)="applyPeriod('week')">Week</button>
      <button type="button" uiButton size="sm" [variant]="period === 'month' ? 'primary' : 'ghost'" (click)="applyPeriod('month')">Month</button>
      <button type="button" uiButton size="sm" [variant]="period === 'custom' ? 'primary' : 'ghost'" (click)="applyPeriod('custom')">Custom</button>
    </div>
  </header>

  @if (period === 'custom') {
    <div class="mb-6 inline-flex gap-2">
      <input type="date" uiInput size="sm" [(ngModel)]="startDate" />
      <input type="date" uiInput size="sm" [(ngModel)]="endDate" />
      <button type="button" uiButton size="sm" (click)="reload()">Apply</button>
    </div>
  }

  @if (loading()) {
    <div class="flex items-center gap-2 text-sm text-muted"><ui-spinner size="sm" /> Loading fees analytics...</div>
  } @else if (summary(); as feeSummary) {
    <section class="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div class="rounded-xl border border-border bg-card p-4">
        <h3 class="mb-2 mt-0 text-base font-semibold">Gross Gain</h3>
        @for (entry of feeSummary.gross_gain_by_currency | keyvalue; track entry.key) {
          <p>{{ entry.key }} {{ formatAmount(entry.value, entry.key) }}</p>
        }
      </div>
      <div class="rounded-xl border border-border bg-card p-4">
        <h3 class="mb-2 mt-0 text-base font-semibold">Provider Cost</h3>
        @for (entry of feeSummary.provider_cost_by_currency | keyvalue; track entry.key) {
          <p>{{ entry.key }} {{ formatAmount(entry.value, entry.key) }}</p>
        }
      </div>
      <div class="rounded-xl border border-border bg-card p-4">
        <h3 class="mb-2 mt-0 text-base font-semibold">Merchant Paid (All Fees)</h3>
        @for (entry of feeSummary.merchant_paid_by_currency | keyvalue; track entry.key) {
          <p>{{ entry.key }} {{ formatAmount(entry.value, entry.key) }}</p>
        }
      </div>
    </section>

    <section class="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div class="rounded-xl border border-border bg-card p-4">
        <div class="mb-2.5 flex items-center justify-between">
          <h2 class="m-0 text-sm font-extrabold">Shortcuts</h2>
          <ui-badge>Configuration</ui-badge>
        </div>
        <ul class="m-0 grid gap-1.5 pl-4">
          <li><a class="font-semibold text-brand no-underline hover:underline" [routerLink]="shellRoutes.commands('manage', 'providers')">Providers configuration</a></li>
          <li><a class="font-semibold text-brand no-underline hover:underline" [routerLink]="shellRoutes.commands('manage', 'entities')">Financial entities</a></li>
          <li><a class="font-semibold text-brand no-underline hover:underline" [routerLink]="shellRoutes.commands('manage', 'journal-entries')">Journal entries</a></li>
          <li><a class="font-semibold text-brand no-underline hover:underline" [routerLink]="shellRoutes.commands('manage', 'accounts')">Accounts</a></li>
        </ul>
      </div>
      <div class="rounded-xl border border-border bg-card p-4">
        <div class="mb-2.5 flex items-center justify-between">
          <h2 class="m-0 text-sm font-extrabold">Fee Types</h2>
          <ui-badge>{{ period | uppercase }}</ui-badge>
        </div>
        @if (feeTypeBreakdown()?.rows?.length) {
          <ul class="m-0 grid list-none gap-2 p-0">
            @for (row of feeTypeBreakdown()?.rows ?? []; track row.code) {
              <li>
                <div class="mb-1 flex justify-between text-sm">
                  <span>{{ row.name }}</span>
                  <span>{{ row.amount }}</span>
                </div>
                <div class="h-1.5 w-full rounded-full bg-surface-container">
                  <div
                    class="h-full rounded-full bg-linear-to-r from-primary to-brand"
                    [style.width.%]="getBreakdownPercent(row.amount, getBreakdownTotal(feeTypeBreakdown()?.rows ?? []))"
                  ></div>
                </div>
              </li>
            }
          </ul>
        } @else {
          <p class="m-0 text-muted">No fee type data for selected period.</p>
        }
      </div>
    </section>

    <section class="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
      <article class="rounded-xl border border-border bg-card p-4">
        <h3 class="mb-2 mt-0 text-base font-semibold">By Currency</h3>
        @for (row of currencyBreakdown()?.rows ?? []; track row.code) {
          <div class="flex justify-between py-0.5">
            <span>{{ row.code }}</span>
            <strong>{{ formatAmount(row.amount, row.code) }}</strong>
          </div>
        }
      </article>
      <article class="rounded-xl border border-border bg-card p-4">
        <h3 class="mb-2 mt-0 text-base font-semibold">By Operation Type</h3>
        @for (row of operationBreakdown()?.rows ?? []; track row.code) {
          <div class="flex justify-between py-0.5">
            <span>{{ row.name }}</span>
            <strong>{{ row.amount }}</strong>
          </div>
        }
      </article>
      <article class="rounded-xl border border-border bg-card p-4">
        <h3 class="mb-2 mt-0 text-base font-semibold">By Fee Type</h3>
        @for (row of feeTypeBreakdown()?.rows ?? []; track row.code) {
          <div class="flex justify-between py-0.5">
            <span>{{ row.name }}</span>
            <strong>{{ row.amount }}</strong>
          </div>
        }
      </article>
    </section>

    <article class="mb-6 rounded-xl border border-border bg-card p-4">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 class="m-0 text-base font-semibold">Fees Structure Configuration</h3>
        <div class="inline-flex items-center gap-2">
          <a uiButton variant="outline" size="sm" [routerLink]="shellRoutes.commands('manage', 'pricing-rules')">Open Fees Configurations</a>
          <button type="button" uiButton size="sm" (click)="openCreateRule()">Create Rule</button>
        </div>
      </div>

      <div class="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-5">
        <div class="flex gap-1.5">
          <button type="button" uiButton size="sm" [variant]="structureTab() === 'table' ? 'primary' : 'ghost'" (click)="setStructureTab('table')">
            Table
          </button>
          <button type="button" uiButton size="sm" [variant]="structureTab() === 'graph' ? 'primary' : 'ghost'" (click)="setStructureTab('graph')">
            Graph
          </button>
        </div>
        @if (structureTab() === 'graph') {
          <input type="text" uiInput size="sm" [(ngModel)]="structureSearch" placeholder="Search by scope, type, rule name..." />
          <select uiInput size="sm" [(ngModel)]="selectedScopeFilter">
            <option value="">All scopes</option>
            @for (scope of scopeChoices; track scope) {
              <option [value]="scope">{{ scope }}</option>
            }
          </select>
          <select uiInput size="sm" [(ngModel)]="selectedFeeTypeFilter">
            <option value="">All fee types</option>
            @for (feeType of feeTypeChoices; track feeType) {
              <option [value]="feeType">{{ feeType }}</option>
            }
          </select>
          <select uiInput size="sm" [(ngModel)]="selectedModeFilter">
            <option value="">All composition modes</option>
            @for (mode of compositionModeChoices; track mode) {
              <option [value]="mode">{{ mode }}</option>
            }
          </select>
        }
      </div>

      @if (structureTab() === 'table') {
        <lib-dynamic-list
          [listPath]="pricingRulesListPath"
          (rowSelect)="onPricingRuleRowSelect($event)"
        />
      } @else {
        <div class="overflow-x-auto rounded-lg border border-border">
          <svg width="1100" height="700" class="bg-surface-muted">
            @for (edge of graphEdges(); track edge.source + '-' + edge.target) {
              @if (getNodeById(edge.source); as sourceNode) {
                @if (getNodeById(edge.target); as targetNode) {
                  <line
                    [attr.x1]="sourceNode.x + 80"
                    [attr.y1]="sourceNode.y + 16"
                    [attr.x2]="targetNode.x - 80"
                    [attr.y2]="targetNode.y + 16"
                    [attr.class]="edge.mode === 'ADDITIVE' ? 'stroke-success stroke-[1.8] [stroke-dasharray:4]' : 'stroke-brand stroke-[1.8]'"
                  />
                }
              }
            }
            @for (node of graphNodes(); track node.id) {
              <g [attr.transform]="'translate(' + node.x + ',' + node.y + ')'">
                <rect x="-80" y="-4" width="170" height="34" rx="8" [attr.class]="node.mode === 'ADDITIVE' ? 'fill-success-bg stroke-success' : 'fill-row-hover stroke-brand'" />
                <text x="-72" y="17" class="fill-fg text-[11px]">{{ node.label }}</text>
              </g>
            }
          </svg>
        </div>
      }
    </article>
  }

  @if (showRuleForm()) {
    <div class="fixed inset-0 z-1000 flex items-center justify-center bg-overlay">
      <section class="max-h-[92vh] w-[min(920px,94vw)] overflow-y-auto rounded-xl border border-border bg-card p-4 text-fg">
        <h3 class="m-0 text-base font-semibold">{{ editingRuleId() ? 'Edit Pricing Rule' : 'Create Pricing Rule' }}</h3>
        <div class="mb-3.5 mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          @for (title of stepTitles; track $index) {
            <button
              type="button"
              class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs"
              [class]="
                isStepCurrent($index + 1)
                  ? 'border-brand bg-row-hover text-brand'
                  : isStepComplete($index + 1)
                    ? 'border-success-border bg-success-bg text-success-fg'
                    : 'border-border bg-surface-muted text-fg-secondary'
              "
              (click)="goToStep($index + 1)"
            >
              <span class="inline-flex size-4.5 items-center justify-center rounded-full bg-surface-container text-[0.65rem] font-bold text-fg">{{ $index + 1 }}</span>
              <span>{{ title }}</span>
            </button>
          }
        </div>
        @if (formError()) {
          <p class="text-sm text-danger-fg">{{ formError() }}</p>
        }
        @if (stepError()) {
          <p class="text-sm text-danger-fg">{{ stepError() }}</p>
        }

        @if (currentStep() === 1) {
          <div class="rounded-lg border border-border bg-surface-muted/50 p-3">
            <h4 class="mb-2 mt-0 text-base font-extrabold">Scope & Target</h4>
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
              <label class="grid min-w-0 gap-1 text-xs">
                Scope
                <select uiInput size="sm" [(ngModel)]="ruleForm.scope" (ngModelChange)="onScopeChange()">
                  @for (scope of scopeChoices; track scope) {
                    <option [value]="scope">{{ scope }}</option>
                  }
                </select>
                <span class="text-[0.7rem] text-muted">{{ helpText['scope'] }}</span>
              </label>
              <label class="grid min-w-0 gap-1 text-xs sm:col-span-2">
                Name
                <input type="text" uiInput size="sm" [(ngModel)]="ruleForm.name" />
                <span class="text-[0.7rem] text-muted">{{ helpText['name'] }}</span>
              </label>
              <label class="grid min-w-0 gap-1 text-xs sm:col-span-2">
                Description
                <textarea rows="3" uiInput size="sm" [(ngModel)]="ruleForm.description"></textarea>
                <span class="text-[0.7rem] text-muted">{{ helpText['description'] }}</span>
              </label>

              @if (ruleForm.scope === 'PROVIDER') {
                <label class="grid min-w-0 gap-1 text-xs sm:col-span-2">
                  Provider
                  <div class="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-1.5">
                    <lib-autocomplete listPath="manage/api-providers/" [value]="providerDisplay" [minLength]="1" placeholder="Search provider..." (itemSelect)="selectProvider($event)" />
                    <button type="button" uiButton variant="outline" size="sm" (click)="clearProvider()">Clear</button>
                  </div>
                  <span class="text-[0.7rem] text-muted">{{ helpText['target'] }}</span>
                </label>
              }
              @if (ruleForm.scope === 'ENTITY') {
                <label class="grid min-w-0 gap-1 text-xs sm:col-span-2">
                  Entity
                  <div class="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-1.5">
                    <lib-autocomplete listPath="finances/entities/" [value]="entityDisplay" [minLength]="1" placeholder="Search entity..." (itemSelect)="selectEntity($event)" />
                    <button type="button" uiButton variant="outline" size="sm" (click)="clearEntity()">Clear</button>
                  </div>
                  <span class="text-[0.7rem] text-muted">{{ helpText['target'] }}</span>
                </label>
              }
              @if (ruleForm.scope === 'MERCHANT') {
                <label class="grid min-w-0 gap-1 text-xs sm:col-span-2">
                  Merchant
                  <div class="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-1.5">
                    <lib-autocomplete listPath="auth/merchants/" [value]="merchantDisplay" [minLength]="1" placeholder="Search merchant..." (itemSelect)="selectMerchant($event)" />
                    <button type="button" uiButton variant="outline" size="sm" (click)="clearMerchant()">Clear</button>
                  </div>
                  <span class="text-[0.7rem] text-muted">{{ helpText['target'] }}</span>
                </label>
              }
              @if (ruleForm.scope === 'MERCHANT_APPLICATION') {
                <label class="grid min-w-0 gap-1 text-xs sm:col-span-2">
                  Merchant Application
                  <div class="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-1.5">
                    <lib-autocomplete listPath="auth/merchant-applications/" [value]="merchantApplicationDisplay" [minLength]="1" placeholder="Search merchant application..." (itemSelect)="selectMerchantApplication($event)" />
                    <button type="button" uiButton variant="outline" size="sm" (click)="clearMerchantApplication()">Clear</button>
                  </div>
                  <span class="text-[0.7rem] text-muted">{{ helpText['target'] }}</span>
                </label>
              }
            </div>
          </div>
        }

        @if (currentStep() === 2) {
          <div class="rounded-lg border border-border bg-surface-muted/50 p-3">
            <h4 class="mb-2 mt-0 text-base font-extrabold">Fee Definition</h4>
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
              <label class="grid min-w-0 gap-1 text-xs">
                Fee Type
                <select uiInput size="sm" [(ngModel)]="ruleForm.fee_type">
                  @for (feeType of feeTypeChoices; track feeType) {
                    <option [value]="feeType">{{ feeType }}</option>
                  }
                </select>
                <span class="text-[0.7rem] text-muted">{{ helpText['fee_type'] }}</span>
              </label>
              <label class="grid min-w-0 gap-1 text-xs">
                Amount Type
                <select uiInput size="sm" [(ngModel)]="ruleForm.amount_type" (ngModelChange)="onAmountTypeChange()">
                  @for (amountType of amountTypeChoices; track amountType) {
                    <option [value]="amountType">{{ amountType }}</option>
                  }
                </select>
                <span class="text-[0.7rem] text-muted">{{ helpText['amount_type'] }}</span>
              </label>
              <label class="grid min-w-0 gap-1 text-xs">
                Value
                <input type="number" uiInput size="sm" [(ngModel)]="ruleForm.value" step="0.0001" />
                <span class="text-[0.7rem] text-muted">{{ helpText['value'] }}</span>
              </label>
              <label class="grid min-w-0 gap-1 text-xs">
                Currency
                <div class="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-1.5">
                  <lib-autocomplete listPath="finances/currencies/" [value]="currencyDisplay" [minLength]="1" placeholder="Search currency..." (itemSelect)="selectCurrency($event)" />
                  <button type="button" uiButton variant="outline" size="sm" (click)="clearCurrency()">Clear</button>
                </div>
                <span class="text-[0.7rem] text-muted">{{ helpText['currency'] }}</span>
              </label>
              <label class="grid min-w-0 gap-1 text-xs">
                Composition Mode
                <select uiInput size="sm" [(ngModel)]="ruleForm.composition_mode">
                  @for (mode of compositionModeChoices; track mode) {
                    <option [value]="mode">{{ mode }}</option>
                  }
                </select>
                <span class="text-[0.7rem] text-muted">{{ helpText['composition_mode'] }}</span>
                <span class="text-[0.68rem] text-brand">{{ getCompositionHint() }}</span>
              </label>
              <label class="grid min-w-0 gap-1 text-xs">
                Parent Rule
                <div class="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-1.5">
                  <lib-autocomplete listPath="finances/pricing-rules/" [value]="parentRuleDisplay" [minLength]="1" placeholder="Search parent rule..." (itemSelect)="selectParentRule($event)" />
                  <button type="button" uiButton variant="outline" size="sm" (click)="clearParentRule()">Clear</button>
                </div>
                <span class="text-[0.7rem] text-muted">{{ helpText['parent_rule'] }}</span>
              </label>
            </div>
          </div>
        }

        @if (currentStep() === 3) {
          <div class="rounded-lg border border-border bg-surface-muted/50 p-3">
            <h4 class="mb-2 mt-0 text-base font-extrabold">Schedule & Activation</h4>
            <div class="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
              <label class="grid min-w-0 gap-1 text-xs">
                Operation Type
                <select uiInput size="sm" [(ngModel)]="ruleForm.operation_type">
                  <option [ngValue]="null">All</option>
                  @for (operationType of operationChoices(); track operationType) {
                    <option [value]="operationType">{{ opsCatalog.label(operationType) }}</option>
                  }
                </select>
                <span class="text-[0.7rem] text-muted">{{ helpText['operation_type'] }}</span>
              </label>
              <label class="grid min-w-0 gap-1 text-xs">
                Effective From
                <input type="datetime-local" uiInput size="sm" [(ngModel)]="ruleForm.effective_from" />
                <span class="text-[0.7rem] text-muted">{{ helpText['effective_from'] }}</span>
              </label>
              <label class="grid min-w-0 gap-1 text-xs">
                Effective Until
                <input type="datetime-local" uiInput size="sm" [(ngModel)]="ruleForm.effective_until" />
                <span class="text-[0.7rem] text-muted">{{ helpText['effective_until'] }}</span>
              </label>
              <label class="grid min-w-0 gap-1 self-start text-xs">
                Active
                <input type="checkbox" class="size-4.5 accent-brand" [(ngModel)]="ruleForm.is_active" />
                <span class="text-[0.7rem] text-muted">{{ helpText['is_active'] }}</span>
              </label>
            </div>
          </div>
        }

        @if (currentStep() === 4) {
          <div class="rounded-lg border border-border bg-card p-3">
            <h4 class="mb-2 mt-0 text-base font-extrabold">Review & Confirm</h4>
            <p class="mb-2 mt-0 text-sm text-muted">Check the configuration below before creating/updating the pricing rule.</p>
            <div class="grid grid-cols-1 gap-x-3 gap-y-2 text-sm sm:grid-cols-2">
              <div><strong>Name:</strong> {{ ruleForm.name || '—' }}</div>
              <div><strong>Description:</strong> {{ resolveDescriptionLabel() }}</div>
              <div><strong>Scope:</strong> {{ ruleForm.scope }}</div>
              <div><strong>Target:</strong> {{ resolveTargetLabel() }}</div>
              <div><strong>Fee Type:</strong> {{ ruleForm.fee_type }}</div>
              <div><strong>Amount:</strong> {{ ruleForm.value }} {{ ruleForm.amount_type === 'PERCENTAGE' ? '%' : (currencyDisplay || '') }}</div>
              <div><strong>Composition:</strong> {{ ruleForm.composition_mode }}</div>
              <div><strong>Parent Rule:</strong> {{ parentRuleDisplay || '—' }}</div>
              <div><strong>Operation:</strong> {{ ruleForm.operation_type || 'ALL' }}</div>
              <div><strong>Effective From:</strong> {{ ruleForm.effective_from || '—' }}</div>
              <div><strong>Effective Until:</strong> {{ ruleForm.effective_until || '—' }}</div>
              <div><strong>Active:</strong> {{ ruleForm.is_active ? 'Yes' : 'No' }}</div>
            </div>
          </div>
        }

        <div class="mt-3 flex justify-end gap-2">
          <button type="button" uiButton variant="outline" (click)="closeRuleForm()">Cancel</button>
          @if (currentStep() > 1) {
            <button type="button" uiButton variant="outline" (click)="prevStep()">Back</button>
          }
          @if (currentStep() < 4) {
            <button type="button" uiButton (click)="nextStep()">Next</button>
          } @else {
            <button type="button" uiButton (click)="saveRule()" [disabled]="savingRule()">
              {{ savingRule() ? 'Saving...' : 'Confirm & Save' }}
            </button>
          }
        </div>
      </section>
    </div>
  }
</section>
